import React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import LoginIcon from '@mui/icons-material/Login';
import { useHashView } from './useHashView';
import { clearHashAuth } from './redeemHash';
import { savePostLoginRedirect } from 'global/utils/loginRedirect';
import { buildShareUrl, IN_SCOPE_PATH_FOR } from './shareLinkConfig';

const Banner = styled('div')(({ theme }) => ({
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: theme.palette.warning.light,
  color: theme.palette.getContrastText(theme.palette.warning.light),
  padding: theme.spacing(1, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(3),
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    padding: theme.spacing(1, 1.5),
    gap: theme.spacing(1),
  },
}));

const BannerText = styled('div')(({ theme }) => ({
  textAlign: 'center',
  fontSize: '1rem',
  lineHeight: 1.35,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.85rem',
  },
}));

const SubText = styled('div')(({ theme }) => ({
  fontStyle: 'italic',
  fontSize: '0.95rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  },
}));

const BoldSpan = styled('span')({
  fontWeight: 700,
});

const BannerButton = styled(Button)(({ theme }) => ({
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    whiteSpace: 'normal',
    width: '100%',
  },
}));

// Friendly display of an expiry value. Backend may send a date-only string or
// an ISO datetime — we parse loosely and fall back to the raw string.
const formatExpiry = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
};

const contentNoun = (kind) => {
  switch (kind) {
    case 'assessment':
      return 'an assessment';
    case 'task':
      return 'a task';
    case 'document':
      return 'a document';
    default:
      return 'content';
  }
};

export default function HashAccessBanner() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { active, hash, scope, expiresAt, loggedIn } = useHashView();

  if (!active) return null;

  const expiry = formatExpiry(expiresAt);
  const noun = contentNoun(scope?.kind);

  const handleLogin = () => {
    // After login, return the user to the ORIGINAL /share/:hash URL rather than the
    // canonical content path. The hash session does not survive the login reload
    // (hashAuth is in the redux-persist blacklist), so routing through the share URL
    // lets ShareRedemptionRoute re-redeem the hash and re-establish the session.
    // This keeps a user WITHOUT normal access on the shared content (with the
    // "you don't normally have access" banner) instead of bouncing them to the list,
    // and lets a user WITH normal access fall through to the canonical view (PFM-2636).
    const shareUrl = buildShareUrl(hash, scope);
    const target = shareUrl || IN_SCOPE_PATH_FOR(scope);
    savePostLoginRedirect({ pathname: target, search: '', hash: '' });
    dispatch(clearHashAuth());
    history.push('/');
  };

  const handleGoToPerform = () => {
    dispatch(clearHashAuth());
    history.push('/home');
  };

  return (
    <Banner data-testid="hash-access-banner">
      <BannerText>
        <div>
          You are viewing {noun} shared via a link that{' '}
          <BoldSpan>expires on {expiry}</BoldSpan>.
        </div>
        {loggedIn ? (
          <SubText>Your profile does not have access to this content normally</SubText>
        ) : (
          <SubText>Login to see more details</SubText>
        )}
      </BannerText>
      {loggedIn ? (
        <BannerButton
          variant="contained"
          color="primary"
          onClick={handleGoToPerform}
          data-testid="hash-banner-cta"
        >
          Go to PERFORM
        </BannerButton>
      ) : (
        <BannerButton
          variant="contained"
          color="primary"
          startIcon={<LoginIcon />}
          onClick={handleLogin}
          data-testid="hash-banner-cta"
        >
          Login
        </BannerButton>
      )}
    </Banner>
  );
}
